import React, { useState } from 'react';
import { Mail, Loader2, Wand, RefreshCw, User2, Tag, ClipboardCheck, ShieldHalf, Star, Users, Copy, Send } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const EMAIL_TYPES = [
    {
        key: 'RiskUpdate',
        label: 'Risk Update',
        icon: ShieldHalf,
        description: 'Executive risk update for leadership and stakeholders.'
    },
    {
        key: 'LeadBrief',
        label: 'Leadership Brief',
        icon: Users,
        description: 'Concise summary for C-level or board communication.'
    },
    {
        key: 'AuditorRes',
        label: 'Auditor Response',
        icon: ClipboardCheck,
        description: 'Formal email to executives about what auditors might find.'
    },
];

const EmailGenTab = ({ scanId }) => {
    const [selectedType, setSelectedType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailData, setEmailData] = useState(null);
    const [error, setError] = useState(null);
    const [cached, setCached] = useState(false);
    const aiHubLink = window.location.origin + window.location.pathname;

    const handleTypeSelect = async (type) => {
        setSelectedType(type);
        setEmailData(null);
        setError(null);
        setLoading(true);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
            const cachedResponse = await fetch(`${ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Email Gen")}?emailType=${type.key}`, { method: 'POST', signal: controller.signal, credentials: 'include' });
            clearTimeout(timeoutId);
            if (cachedResponse.status === 200) {
                setCached(true);
                const json = await cachedResponse.json();
                const x = JSON.parse(json.response);
                setEmailData({
                    ...x,
                    tokensUsed: json.tokens_used,
                    costUsd: json.cost_usd,
                    lastUpdated: json.lastUpdated,
                });
            } else {
                await handleGenerate(type);
            }
        } catch (err) {
            setError('Unable to retrieve AI-generated email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async (type) => {
        try {
            setCached(false);
            setEmailData(null);
            setError(null);
            setLoading(true);
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000);
            const response = await fetch(ApiNames.AI_HUB_EMAIL_GEN(scanId, type.key), { method: 'POST', signal: controller.signal, credentials: 'include' });
            clearTimeout(timeoutId);
            if (!response.ok) throw new Error('Request failed');
            const json = await response.json();
            const _emailData = JSON.parse(json.response);
            setEmailData({
                ..._emailData,
                tokensUsed: json.tokens_used,
                costUsd: json.cost_usd,
                lastUpdated: json.lastUpdated,
            });
        } catch (err) {
            setError('Unable to generate AI-generated email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatEmailBody = () => {
        const placeholder = '[GovCompliance AI Hub Link]';
        const parts = emailData.body.split(placeholder);

        return parts.reduce((acc, part, i) => {
            if (i === 0) return [part];
            return [
                ...acc,
                <a
                    key={i}
                    href={aiHubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                >
                    {aiHubLink}
                </a>,
                part
            ];
        }, []);
    };

    const copyEmailToClipboard = async () => {
        try {
            // Write the text to the clipboard
            await navigator.clipboard.writeText(emailData.body.replace('[GovCompliance AI Hub Link]', aiHubLink));
        } catch (err) {
            // Fallback or error handling can go here
            alert('Unabled to copy email.');
        }
    };

    const sendEmailNow = async () => {
        const recipient = emailData.to;
        const subject = emailData.subject;
        const body = emailData.body.replace('[GovCompliance AI Hub Link]', aiHubLink);
        const priority = emailData.priority;
        // Encode the body to handle special characters correctly
        const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}&X-Priority=${priority}&Importance=${priority}`;

        // Open the default email client
        window.location.href = mailtoLink;
    }

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-6xl">
                <div className="card-header flex items-center justify-between">
                    <div className="card-title flex items-center gap-2">
                        <Mail className="w-6 h-6 text-blue-400" />
                        <h3 className="font-bold text-lg">AI Email Generator</h3>
                    </div>
                    <span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold"><Wand className="w-4 h-4 mr-1" />AI-Powered</span>
                </div>
                <p className="text-gray-400 mb-6 text-sm mt-2">
                    Select an email type to generate a compliance update or response. Emails are cached for speed and cost savings, but can be regenerated.
                </p>
                <div className="persona-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
                    {EMAIL_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                            <button
                                key={type.key}
                                className={`persona-card flex flex-col items-center p-6 rounded-xl border-2 transition-all duration-200 shadow-sm cursor-pointer focus:outline-none h-full ${selectedType?.key === type.key ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-105' : 'border-base-300 bg-base-100 hover:border-blue-400'}`}
                                onClick={() => handleTypeSelect(type)}
                                disabled={loading}
                                type="button"
                            >
                                <span className="persona-icon mb-2">
                                    <Icon className="w-8 h-8 text-blue-400" />
                                </span>
                                <h4 className="font-semibold text-base mb-1 text-center">{type.label}</h4>
                                <p className="text-xs text-gray-400 text-center mt-1">{type.description}</p>
                            </button>
                        );
                    })}
                </div>
                {/* AI Email Response Box */}
                <div className="w-full">
                    {loading && (
                        <div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
                            <Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
                            <span className="text-gray-400">Generating AI email...</span>
                        </div>
                    )}
                    {!loading && error && (
                        <div className="ai-response text-center text-red-400 py-12">{error}</div>
                    )}
                    {!loading && !error && emailData && (
                        <div className="ai-response bg-base-100 rounded-xl shadow-xl p-6 animate-fadeIn">
                            <div className="flex flex-col gap-2 mb-4">
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <User2 className="w-4 h-4 text-blue-400" />
                                    <span><b>To:</b> {emailData.to}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Tag className="w-4 h-4 text-blue-400" />
                                    <span><b>Subject:</b> {emailData.subject}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Star className="w-4 h-4 text-blue-400" />
                                    <span><b>Priority:</b> {emailData.priority}</span>
                                </div>
                            </div>
                            <div className="bg-base-200 rounded-lg p-4 mb-4">
                                <pre className="whitespace-pre-wrap text-black-400 text-sm" style={{ fontFamily: 'inherit' }}>{formatEmailBody()}</pre>
                            </div>
                            <div className="flex flex-wrap gap-4 text-xs text-gray-400 mb-2">
                                <span>Tokens used: {emailData.tokensUsed}</span>
                                <span>Cost: ${emailData.costUsd}</span>
                                {cached && (
                                    <span>Viewing cached email. Last updated: {emailData.lastUpdated}</span>
                                )}
                            </div>
                            <div className="flex flex-row gap-x-2">
                                <button
                                    className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150 flex items-center gap-2"
                                    onClick={() => copyEmailToClipboard()}
                                    disabled={loading || !scanId}
                                >
                                    <Copy className="w-4 h-4 mr-1" /> Copy to Clipboard
                                </button>
                                <button
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150 flex items-center gap-2"
                                    onClick={() => sendEmailNow()}
                                    disabled={loading || !scanId}
                                >
                                    <Send className="w-4 h-4 mr-1" /> Send Now
                                </button>
                                <button
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150 flex items-center gap-2"
                                    onClick={() => handleGenerate(selectedType)}
                                    disabled={loading || !scanId}
                                >
                                    <RefreshCw className="w-4 h-4 mr-1" /> Regenerate Email
                                </button>
                            </div>
                        </div>
                    )}
                    {!loading && !error && !emailData && (
                        <div className="ai-response text-center text-gray-400 py-12">
                            Select an email type to generate an AI-powered compliance email.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmailGenTab;
